import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Dumbbell, Check, ArrowRight, Sparkles, Users, X } from "lucide-react";
import RankBadge from "../components/RankBadge";
import { useCurrency, setCurrencyOverride, perDayLabel } from "../lib/currency";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const BULLETS = [
    "Mechanical tension is the driver — not the pump",
    "4–8 reps, double progression, RIR 1–2",
    "4–6 hard sets per muscle per week. No junk volume",
    "Joint-friendly stable exercises. In-and-out sessions",
];

const PREVIEWS = [
    {
        src: "/preview-dashboard.png",
        eyebrow: "Control Room",
        title: "Every set, tracked. Every week, measured.",
        body:
            "Total tonnage, last-30-day cadence, current split and mode, weekly strength chart, current rank — at a glance. No spreadsheets. No guessing if you're progressing.",
    },
    {
        src: "/preview-exercise.png",
        eyebrow: "60+ stable, joint-friendly exercises",
        title: "Setup, execution, mistakes — for every lift.",
        body:
            "Looped form demos, anatomical muscle maps that highlight what each movement actually trains, and curated variants so you know exactly when to swap. Built for lifters who want results, not soreness.",
    },
    {
        src: "/preview-ranks.png",
        eyebrow: "20 ranks. One way up.",
        title: "Every working set moves you.",
        body:
            "No daily login bonuses, no boosts, no shortcuts. Earn EXP by adding weight, adding reps, hitting PRs. Twenty ranks across six tiers — Foundation to Legend. Find out who you actually are.",
    },
];

const FAQ = [
    {
        q: "How is this different from other hypertrophy apps?",
        a: "Most apps optimise for variety and gimmicks: 12-week 'phases', auto-deload, new exercises every cycle. Hypergains optimises for the only variable proven to drive growth — mechanical tension on stable exercises you can overload for years. Two split options, 4–8 reps, low effective volume, joint-friendly lifts. That's the whole system. If you want endless rotation, this isn't for you.",
    },
    {
        q: "Do I need a gym or special equipment?",
        a: "A standard commercial gym membership covers everything. Most lifts are machines, cables, and stable barbell/dumbbell movements — chosen because they let you load progressively without fighting balance. A few unilateral options (single-arm chest press, single-leg RDL) are included for side-imbalance work, but nothing requires exotic kit.",
    },
    {
        q: "What if I'm a brand-new lifter?",
        a: "Hypergains works on day one. The onboarding quiz recommends the Full Body 3-day split for beginners, every exercise has setup/execution/mistakes guidance with looped form demos, and the workout logger enforces the double-progression rule automatically. The principles don't change as you advance — only the loads do.",
    },
    {
        q: "Can I cancel anytime?",
        a: "Yes. Every plan auto-renews to keep your access uninterrupted, and you can cancel from Account → Manage subscription in one tap — no calls, no email forms. We send a heads-up email 7 days before any quarterly or annual renewal so there are no surprises. The 7-day trial requires no credit card.",
    },
    {
        q: "Why no cardio or conditioning module?",
        a: "Because adding 'cardio days' to a hypertrophy program usually steals recovery from the lifts that actually grow you. Your fat-loss mode tracks daily steps (6k–15k targets by goal) plus a calorie deficit — that's the conditioning that doesn't compromise your training. If you want HIIT bro splits, there are 200 other apps for that.",
    },
    {
        q: "Is this safe for older lifters or anyone with joint issues?",
        a: "Yes — exercise selection is deliberately joint-friendly. Stable machines, cables, and chest-supported movements dominate the library precisely because they let you push intensity without grinding your shoulders, knees, or low back. Warm-up protocols (2 warm-ups on first lift per muscle, 1 on subsequent) are baked into every session.",
    },
    {
        q: "Does it work for natural and enhanced lifters?",
        a: "Same physiology, same principles. Mechanical tension drives hypertrophy regardless of hormonal status. Enhanced lifters tolerate more total volume, naturals tolerate less — but both grow most efficiently on the same exercise selection and rep ranges. The program scales with how hard you can recover, not with what's in your blood.",
    },
    {
        q: "I just want to get toned, not bulky — is this still for me?",
        a: "Yes — because \"toned\" isn't a separate training method, it's the same one. The look people describe as toned is simply more lean muscle plus less body fat. That's exactly what mechanical tension on stable lifts (build the muscle) paired with a calorie deficit and step target (drop the fat) produces. Light weights for high reps don't sculpt anything — they just give you a temporary pump and burn fewer calories than heavier work. And worrying about getting too big from lifting is like worrying you'll accidentally write a concerto your first time sitting at a piano. Muscle is hard-earned over years of deliberate work; you can stop adding weight whenever you've reached the look you want.",
    },
];

// ─── Ad-attribution → hero-copy adaptation ────────────────────────────────
// Detects goal intent from URL params so the landing can present hyper-relevant
// hero copy that mirrors what the Meta / Google ad promised. Cognitive
// continuity (ad → landing → funnel using the same words) typically lifts
// conversion 12-18% on cold ad traffic.
//
// Precedence: explicit ?goal= param wins > utm_content > utm_campaign.
// Keyword mapping is intentionally loose because ad managers use different
// naming conventions ("fatloss", "cut", "shred" all map to lose etc.).
function detectGoalFromUrl(paramsIn) {
    try {
        const params = paramsIn || new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        const explicit = (params.get("goal") || "").toLowerCase().trim();
        if (["build", "lose", "recomp"].includes(explicit)) return explicit;
        const haystack = [params.get("utm_content"), params.get("utm_campaign")].filter(Boolean).join(" ").toLowerCase();
        if (!haystack) return null;
        // Recomp is checked first because "recomp fat loss" contains both signals.
        if (/\b(recomp|body\s*recomp|both)\b/.test(haystack)) return "recomp";
        if (/(fat|lose|cut|shred|lean|deficit)/.test(haystack)) return "lose";
        if (/(muscle|bulk|build|mass|hyper|strength|size|growth)/.test(haystack)) return "build";
        return null;
    } catch { return null; }
}

const HERO_VARIANTS = {
    default: {
        eyebrow: "Evidence · Overload · Stimulate & Go Home",
        h1: <>Stop chasing the pump.<br /><span className="text-[#ff3b30]">Start building muscle</span> that <span className="border-b-4 border-[#ff3b30]">actually</span> grows.</>,
        sub: "The evidence-based training system for lifters who are done wasting years on programs that don't progress them. Mechanical tension. 4–8 reps. Double progression. 4–6 hard sets per muscle. In and out in under an hour — natural, enhanced, beginner or advanced.",
    },
    build: {
        eyebrow: "Heavy lifting · Mechanical tension · Real growth",
        h1: <>Stop guessing.<br /><span className="text-[#ff3b30]">Start building</span> actual <span className="border-b-4 border-[#ff3b30]">muscle</span>.</>,
        sub: "Heavy 4–8 rep work on stable, joint-friendly lifts. 4–6 hard sets per muscle per week. Double progression that shows on the scale AND in the mirror. Built on the studies you already trust — Schoenfeld, Baz-Valle, Refalo. Not the influencer of the week.",
    },
    lose: {
        eyebrow: "Cut smart · Keep every gain",
        h1: <>Stop losing muscle<br /><span className="text-[#ff3b30]">every time</span> you <span className="border-b-4 border-[#ff3b30]">cut</span>.</>,
        sub: "High-protein deficit + heavy lifting + a real step target. The exact protocol that dropped 4.8 kg of fat AND added 1.2 kg of muscle simultaneously in Longland 2016. No 30-day gimmicks. No cardio-bro splits. No lost gains.",
    },
    recomp: {
        eyebrow: "Drop fat · Add muscle · Same month",
        h1: <>Look better in 12 weeks.<br /><span className="text-[#ff3b30]">Both directions</span> at <span className="border-b-4 border-[#ff3b30]">once</span>.</>,
        sub: "The recomp protocol validated in Longland 2016: subjects dropped 4.8 kg of fat AND gained 1.2 kg of lean mass — simultaneously. Same evidence, now in your pocket, tracked daily, adjusted weekly.",
    },
};

// Trust strip — appears directly under hero. Concrete, verifiable claims only.
// Every chip below maps to a real feature in the app (60+ exercises, 20 ranks,
// 7-day trial, no card, etc.). This is the "de-risk everything" band that
// converts the hero click.
const TRUST_CHIPS = [
    "60+ exercises · form demos",
    "20-rank progression system",
    "7-day trial · no card",
    "Cancel in 2 taps",
    "Built on peer-reviewed research",
];

// Recruit overlay — appears when an invite link drops the visitor on the
// landing page. Persists the context to localStorage so the squad join
// happens automatically after the new user signs up + verifies email.
function RecruitOverlay({ rank, from, squadCode, onDismiss }) {
    if (!rank && !squadCode) return null;
    return (
        <div className="border-b border-accent-red/40 bg-gradient-to-b from-accent-red/15 via-accent-red/5 to-transparent" data-testid="recruit-overlay">
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
                    {rank ? (
                        <div className="flex-none">
                            <RankBadge rankId={rank.id} size="lg" />
                        </div>
                    ) : (
                        <div className="flex h-20 w-20 flex-none items-center justify-center rounded-full border-2 border-accent-red bg-accent-red/10 text-accent-red">
                            <Users className="h-9 w-9" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="label-eyebrow text-accent-red flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5" />
                            {rank ? "Rank received · sneak peek" : "Squad invite"}
                        </div>
                        <h2 className="mt-1 font-display text-2xl font-black uppercase leading-tight text-white sm:text-3xl" data-testid="recruit-headline">
                            {rank
                                ? <>
                                    {from ? `${from}` : "A lifter"} just hit{" "}
                                    <span className="text-accent-red">{rank.name}</span>.
                                    {" "}<span className="text-white/85">Find out where you stand.</span>
                                </>
                                : <>
                                    {from ? `${from} called you up` : "You've been called up"}.{" "}
                                    <span className="text-accent-red">Code: {squadCode}</span>
                                </>
                            }
                        </h2>
                        {rank && (
                            <p className="mt-1 italic text-white/75" data-testid="recruit-tagline">"{rank.tagline}"</p>
                        )}
                        {!rank && (
                            <p className="mt-1 text-sm text-white/75">5-person training pods. Weekly EXP + tonnage leaderboard. No DMs, just receipts.</p>
                        )}
                    </div>
                    <div className="flex flex-none items-center gap-2">
                        <Link to="/register" data-testid="recruit-cta" className="rounded-sm bg-accent-red px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]">
                            Start free trial <ArrowRight className="ml-1 inline h-3.5 w-3.5" />
                        </Link>
                        <button onClick={onDismiss} data-testid="recruit-dismiss" aria-label="Dismiss" className="rounded-sm border border-white/20 p-2 text-white/60 hover:bg-white/5 hover:text-white">
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Landing() {
    const [params] = useSearchParams();
    const [overlay, setOverlay] = useState(null);
    const currency = useCurrency();
    const perDay = perDayLabel("monthly", currency);
    const perDayAnnual = perDayLabel("annual", currency);

    // Ad-adaptive hero: detect goal intent from URL and swap the hero copy
    // (and CTAs) accordingly. Cold ad traffic converts on cognitive
    // continuity between the ad and the landing page.
    const goalMatch = detectGoalFromUrl(params);
    const heroVariant = HERO_VARIANTS[goalMatch] || HERO_VARIANTS.default;
    // Preserve URL params through internal CTAs so the goal + attribution
    // survive across the funnel.
    const queryPassthrough = (typeof window !== "undefined" && window.location.search) || "";
    const onboardingHref = goalMatch
        ? `/onboarding?goal=${goalMatch}${queryPassthrough ? "&" + queryPassthrough.slice(1) : ""}`
        : `/onboarding${queryPassthrough}`;

    // Anonymous funnel: track every landing page mount (server-side dedup'd
    // to 1 event per visitor per 30 min — refresh spam doesn't inflate).
    useEffect(() => {
        import("../lib/funnel").then(({ trackEvent }) => trackEvent("landing_view"));
    }, []);

    useEffect(() => {
        const inviteRankId = parseInt(params.get("invite_rank") || "0", 10);
        const squadCode = params.get("squad_code") || params.get("code") || "";
        const from = params.get("from") || "";
        // Nothing relevant → no overlay
        if (!inviteRankId && !squadCode) return;

        // Persist invite context so after signup the squad join can fire.
        if (squadCode) {
            try {
                localStorage.setItem("hg_invite_squad", JSON.stringify({ code: squadCode.toUpperCase(), from, ts: Date.now() }));
            } catch (e) { /* ignore */ }
        }
        if (inviteRankId) {
            try {
                localStorage.setItem("hg_invite_rank", JSON.stringify({ id: inviteRankId, from, ts: Date.now() }));
            } catch (e) { /* ignore */ }
        }

        // Look up rank info from the public endpoint so we can render the
        // correct tagline/insignia. Endpoint is anonymous + cacheable.
        if (inviteRankId) {
            fetch("/api/ranks/public")
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    const rank = (d?.ranks || []).find(r => r.id === inviteRankId);
                    setOverlay({ rank, squadCode: squadCode.toUpperCase(), from });
                })
                .catch(() => setOverlay({ rank: null, squadCode: squadCode.toUpperCase(), from }));
        } else {
            setOverlay({ rank: null, squadCode: squadCode.toUpperCase(), from });
        }
    }, [params]);

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white">
            {/* top bar */}
            <header className="border-b border-white/10">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
                    <div className="flex items-center gap-2" data-testid="landing-logo">
                        <Dumbbell className="h-6 w-6 text-[#ff3b30]" />
                        <span className="font-display text-2xl font-black uppercase tracking-tight">Hyper<span className="text-[#ff3b30]">gains</span></span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/login" className="text-xs font-bold uppercase tracking-wider text-white/80 hover:text-white" data-testid="landing-signin">Sign in</Link>
                        <Link to="/onboarding" className="rounded-sm bg-[#ff3b30] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#d32f26]" data-testid="landing-trial">Start free trial</Link>
                    </div>
                </div>
            </header>

            {overlay && (
                <RecruitOverlay
                    rank={overlay.rank}
                    from={overlay.from}
                    squadCode={overlay.squadCode}
                    onDismiss={() => setOverlay(null)}
                />
            )}

            {/* PROOF STRIP — always above the fold. Frames the app as legitimate + risk-free. */}
            <div className="border-b border-white/10 bg-white/[0.02]" data-testid="landing-proof-strip">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 py-4 text-center sm:px-6">
                    <div className="flex items-baseline gap-2">
                        <span className="font-black text-white">Peer-reviewed</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/55">principles · not gym-bro folklore</span>
                    </div>
                    <div className="hidden h-4 w-px bg-white/15 sm:block" />
                    <div className="flex items-baseline gap-2">
                        <span className="font-black text-white">Hypertrophy</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/55">or fat loss · you pick</span>
                    </div>
                    <div className="hidden h-4 w-px bg-white/15 sm:block" />
                    <div className="flex items-baseline gap-2">
                        <span className="font-black text-white">7 days</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/55">Free · No card required</span>
                    </div>
                    <div className="hidden h-4 w-px bg-white/15 sm:block" />
                    <div className="flex items-baseline gap-2">
                        <span className="font-black text-white">Any gym</span>
                        <span className="text-[10px] uppercase tracking-widest text-white/55">No exotic kit needed</span>
                    </div>
                </div>
            </div>

            {/* BRANCHING CTA — cold ad traffic picks the goal that matches
                what the ad promised. Their answer pre-fills Q1 of the quiz
                and skips them past the hook straight into the funnel.
                When a goal was already declared via URL (ad campaign),
                that path becomes the dominant card instead of the default
                recomp — reinforcing the ad's promise. */}
            <section className="border-b border-white/10 bg-[#0a0a0a]" data-testid="landing-branch">
                <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
                    <div className="mb-8 text-center">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">{goalMatch ? "Locked in" : "Pick your target"}</div>
                        <h2 className="mx-auto max-w-3xl font-display text-3xl font-black uppercase leading-[0.98] sm:text-5xl">
                            {goalMatch === "build" && "Your muscle-building plan is 60 seconds away."}
                            {goalMatch === "lose" && "Your fat-loss plan is 60 seconds away."}
                            {goalMatch === "recomp" && "Your recomp plan is 60 seconds away."}
                            {!goalMatch && "What do you want the next 12 weeks to build?"}
                        </h2>
                        <p className="mx-auto mt-3 max-w-xl text-sm text-white/65 sm:text-base">
                            {goalMatch
                                ? "We'll personalize your training split, calories, macros, and step target — all built around your target."
                                : "Tap the goal that matches your training. Your plan gets built around it — training split, calories, macros, step target, everything."
                            }
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        {[
                            {
                                key: "build",
                                eyebrow: "Path 01",
                                title: "Build muscle",
                                pitch: "Add mass. Get stronger. Look bigger in the mirror without soft-bulking into a food coma.",
                                bullets: ["Heavy 4–8 rep compound work", "Mild caloric surplus + 1.6 g/kg protein (Morton 2018)", "Upper/Lower or Full Body split — 3-5 days/wk"],
                                cta: "Build muscle →",
                            },
                            {
                                key: "recomp",
                                eyebrow: goalMatch ? "Path 02" : "Path 02 · most popular",
                                title: "Both — recomp",
                                pitch: "Drop fat AND add muscle at the same time. Slower per direction but you look better twice as fast.",
                                bullets: ["Maintenance calories + high protein", "Same evidence-based training", "Progress tracked in strength + waist"],
                                cta: "Recomp me →",
                            },
                            {
                                key: "lose",
                                eyebrow: "Path 03",
                                title: "Lose fat",
                                pitch: "Drop bodyfat while keeping every hard-earned kilo of muscle. No 30-day gimmicks.",
                                bullets: ["18% caloric deficit + high protein", "Resistance training to preserve muscle", "Daily step target + weekly progress checks"],
                                cta: "Lose fat →",
                            },
                        ].map((p) => {
                            // Dominant card: the one that matches the URL goal, or recomp by default.
                            const dominant = goalMatch ? p.key === goalMatch : p.key === "recomp";
                            const badgeLabel = goalMatch ? "Your pick" : "Most popular";
                            return (
                            <Link
                                key={p.key}
                                to={`/onboarding?goal=${p.key}${queryPassthrough ? "&" + queryPassthrough.slice(1) : ""}`}
                                data-testid={`branch-${p.key}`}
                                className={`group relative flex flex-col justify-between border p-6 transition-all sm:p-7 md:min-h-[380px] ${
                                    dominant
                                        ? "border-[#ff3b30] bg-gradient-to-b from-[#ff3b30]/12 via-[#ff3b30]/4 to-transparent shadow-[0_0_60px_-12px_rgba(255,59,48,0.45)] md:-translate-y-2 md:scale-[1.02]"
                                        : "border-white/12 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]"
                                }`}
                            >
                                {dominant && (
                                    <div
                                        className="absolute -top-3 left-1/2 -translate-x-1/2 select-none whitespace-nowrap border border-[#ff3b30] bg-[#ff3b30] px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-white shadow-[0_4px_18px_-4px_rgba(255,59,48,0.7)]"
                                        data-testid={`branch-${p.key}-badge`}
                                    >
                                        {badgeLabel}
                                    </div>
                                )}
                                <div>
                                    <div className="label-eyebrow text-[#ff3b30]">{p.eyebrow}</div>
                                    <h3 className="mt-3 font-display text-3xl font-black uppercase leading-none sm:text-4xl">{p.title}</h3>
                                    <p className="mt-4 text-sm leading-relaxed text-white/75 sm:text-[15px]">{p.pitch}</p>
                                    <ul className="mt-5 space-y-2">
                                        {p.bullets.map((b, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs text-white/85 sm:text-sm">
                                                <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-[#ff3b30]" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className={`mt-6 flex items-center justify-between rounded-sm px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all ${
                                    dominant
                                        ? "bg-[#ff3b30] text-white group-hover:bg-[#d32f26]"
                                        : "border border-white/20 text-white group-hover:border-white/40 group-hover:bg-white/5"
                                }`}>
                                    <span>{p.cta}</span>
                                    <ArrowRight className="h-4 w-4" />
                                </div>
                            </Link>
                        );})}
                    </div>

                    <p className="mt-6 text-center text-[11px] uppercase tracking-widest text-white/45">
                        {goalMatch
                            ? "Changed your mind? Tap any other path — nothing's locked in."
                            : "Not sure? Just pick one — you can change your goal any time from Account."
                        }
                    </p>
                </div>
            </section>

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: "url(https://images.unsplash.com/photo-1770513649465-2c60c8039806?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/85 to-[#0a0a0a]" />
                <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:py-32">
                    <div className="lg:col-span-8">
                        <div className="label-eyebrow mb-6 text-[#ff3b30]" data-testid="hero-eyebrow">{heroVariant.eyebrow}</div>
                        <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-h1">
                            {heroVariant.h1}
                        </h1>
                        <p className="mt-6 max-w-2xl text-base text-white/70 sm:text-lg" data-testid="hero-sub">
                            {heroVariant.sub}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <Link to={onboardingHref} data-testid="hero-trial" className="flex items-center gap-2 rounded-sm bg-[#ff3b30] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-[#d32f26]">
                                {goalMatch === "lose" ? "Build my fat-loss plan — 60 sec" : goalMatch === "build" ? "Build my mass plan — 60 sec" : goalMatch === "recomp" ? "Build my recomp plan — 60 sec" : "Build my plan — 60 sec"} <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/pricing" data-testid="hero-pricing" className="rounded-sm border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/5">
                                See pricing · ~{perDay} {currency}
                            </Link>
                        </div>
                        <p className="mt-4 max-w-xl text-sm leading-snug text-white/80">
                            Most lifters waste <span className="font-bold text-white">2–3 years</span> on programs that don&apos;t actually progress them. Don&apos;t be one of them.
                        </p>
                        <p className="mt-3 text-xs uppercase tracking-widest text-white/50">No card required for trial · Cancel anytime · All you need is a gym membership</p>

                        {/* Trust chip strip — de-risks the hero click. Every
                            claim maps to a real feature/policy. */}
                        <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-trust-chips">
                            {TRUST_CHIPS.map((chip, i) => (
                                <span
                                    key={i}
                                    className="inline-flex items-center gap-1.5 border border-white/15 bg-white/[0.03] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white/75"
                                    data-testid={`trust-chip-${i}`}
                                >
                                    <Check className="h-3 w-3 text-[#ff3b30]" />
                                    {chip}
                                </span>
                            ))}
                        </div>

                        {/* Currency indicator + tiny toggle — matches what
                            the visitor will see on the Pricing page. Auto-
                            detection is ~95%; the visible toggle recovers
                            the 5% edge cases (VPN, dual citizens, ad-block
                            corrupting the server geo response). */}
                        <div className="mt-5 inline-flex items-center gap-1 border border-white/10 bg-white/[0.02] p-1" data-testid="hero-currency-toggle">
                            <span className="pl-2 pr-1 text-[9px] uppercase tracking-widest text-white/40">Prices in</span>
                            {["USD", "CAD"].map(c => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setCurrencyOverride(c)}
                                    data-testid={`hero-currency-${c.toLowerCase()}`}
                                    aria-pressed={currency === c}
                                    className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest transition-colors ${currency === c ? "bg-[#ff3b30] text-white" : "text-white/60 hover:text-white"}`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="border border-white/15 bg-black/40 p-6 backdrop-blur">
                            <div className="label-eyebrow mb-4 text-white/60">The system</div>
                            <ul className="space-y-4">
                                {BULLETS.map((b, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <Check className="mt-0.5 h-5 w-5 flex-none text-[#ff3b30]" />
                                        <span className="text-sm text-white/80">{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* APP PREVIEWS — what you actually get */}
            <section className="border-b border-white/10 bg-[#0a0a0a]" data-testid="landing-previews">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="mb-12 max-w-2xl">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">What you actually get</div>
                        <h2 className="font-display text-4xl font-black uppercase leading-[1] sm:text-5xl">Built for lifters. Not for the App Store.</h2>
                    </div>
                    <div className="space-y-20">
                        {PREVIEWS.map((p, i) => (
                            <div
                                key={i}
                                className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16 ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
                            >
                                <div className="relative overflow-hidden border border-white/10 bg-black/60 shadow-[0_30px_80px_-20px_rgba(255,59,48,0.25)]">
                                    <img
                                        src={p.src}
                                        alt={p.title}
                                        loading="lazy"
                                        className="block w-full"
                                    />
                                    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/5" />
                                </div>
                                <div>
                                    <div className="label-eyebrow mb-3 text-[#ff3b30]">{p.eyebrow}</div>
                                    <h3 className="font-display text-3xl font-black uppercase leading-tight sm:text-4xl">{p.title}</h3>
                                    <p className="mt-4 text-white/70 sm:text-lg">{p.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="border-b border-white/10 bg-[#0a0a0a]">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
                        <div>
                            <div className="label-eyebrow mb-3 text-[#ff3b30]">What actually works</div>
                            <h2 className="font-display text-4xl font-black uppercase leading-[1] sm:text-5xl">Mechanical tension. Not metabolic noise.</h2>
                        </div>
                        <div className="space-y-4 text-white/70">
                            <p>Soreness, the pump, the burn — those are byproducts. The primary driver of hypertrophy is mechanical tension: heavy loads, fully recruited high-threshold motor units, reps close to failure. That's the signal your muscles respond to.</p>
                            <p>Everything in Hypergains is built to deliver that signal efficiently: stable exercises so you can overload them for years, a 4–8 rep range so every rep is a stimulating rep, and just enough sets to grow — not enough to bury you in fatigue.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS */}
            <section className="border-b border-white/10">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-0 px-4 sm:px-6 md:grid-cols-3 md:divide-x md:divide-white/10">
                    {[
                        { h: "01 · Split", t: "Upper/Lower (4 days) or Full Body (3 days). 24h recovery baked in." },
                        { h: "02 · Load", t: "4–8 reps. 2–3 working sets. 1–2 Reps In Reserve. Double progression." },
                        { h: "03 · Recover", t: "Low effective volume. Rest days. 6k–10k steps. Protein target. Sleep." },
                    ].map((p, i) => (
                        <div key={i} className="py-12 md:px-10">
                            <div className="label-eyebrow mb-3 text-[#ff3b30]">{p.h}</div>
                            <p className="font-display text-2xl font-black uppercase leading-tight">{p.t}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SCIENCE — 6 peer-reviewed principles the app is built on. Real
                citations only, split roughly evenly between hypertrophy and
                fat-loss / body-composition research so both targets are covered. */}
            <section className="border-b border-white/10 bg-[#0a0a0a]" data-testid="landing-science">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="mb-10 max-w-2xl">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">The science</div>
                        <h2 className="font-display text-4xl font-black uppercase leading-[1] sm:text-5xl">Built on peer-reviewed research. Not gym-bro folklore.</h2>
                        <p className="mt-4 text-sm text-white/70 sm:text-base">
                            Every training and nutrition principle inside Hypergains traces back to a specific paper. Six of them are the load-bearing beams. Here they are.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {[
                            {
                                tag: "Hypertrophy",
                                claim: "Mechanical tension is the primary driver of muscle growth.",
                                body: "Metabolic stress and muscle damage are downstream. Load moved with intent under high tension is what grows tissue.",
                                cite: "Schoenfeld, B.J. (2010). J. Strength & Conditioning Research 24(10):2857-72.",
                            },
                            {
                                tag: "Hypertrophy",
                                claim: "Heavy 4–8 reps grow muscle as effectively as light high-rep sets — when volume is matched.",
                                body: "Meta-analysis of 21 RCTs found no meaningful hypertrophy difference between load ranges. Heavier is more time-efficient and better for strength carryover.",
                                cite: "Schoenfeld, B.J. et al. (2017). J. Strength & Conditioning Research 31(12):3508-3523.",
                            },
                            {
                                tag: "Hypertrophy",
                                claim: "4–10 hard sets per muscle per week is enough. More is not always better.",
                                body: "Systematic review shows returns plateau after ~10 sets/muscle/week for most lifters. Junk volume steals recovery.",
                                cite: "Baz-Valle, E. et al. (2022). J. Human Kinetics 81:199-210.",
                            },
                            {
                                tag: "Hypertrophy",
                                claim: "Train close to failure. 0–3 reps in reserve is the growth zone.",
                                body: "Sets taken further from failure produce less hypertrophy per set. RIR 0-3 maximizes stimulus without burying recovery.",
                                cite: "Refalo, M.C. et al. (2023). Sports Medicine 53(3):649-665.",
                            },
                            {
                                tag: "Fat loss",
                                claim: "High protein + resistance training preserves muscle in a deficit — and can add muscle while dropping fat.",
                                body: "Landmark recomp study: subjects lost 4.8 kg fat AND gained 1.2 kg lean mass on a 40% deficit with 2.4 g/kg protein and heavy lifting.",
                                cite: "Longland, T.M. et al. (2016). American J. Clinical Nutrition 103(3):738-746.",
                            },
                            {
                                tag: "Fat loss",
                                claim: "NEAT — daily steps and low-intensity movement — is the biggest fat-loss lever beyond diet.",
                                body: "Between-person differences in daily NEAT explain up to 2,000 kcal/day of variance. Not cardio. Steps. Every day.",
                                cite: "Levine, J.A. et al. (2005). Science 307(5709):584-586.",
                            },
                        ].map((s, i) => (
                            <div key={i} className="flex flex-col border border-white/10 bg-white/[0.02] p-6 transition-all hover:border-white/25" data-testid={`science-${i}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="font-display text-3xl font-black text-[#ff3b30]">0{i + 1}</span>
                                    <span className="rounded-sm border border-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white/70">{s.tag}</span>
                                </div>
                                <h3 className="mt-4 font-display text-lg font-black uppercase leading-tight text-white sm:text-xl">{s.claim}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/70">{s.body}</p>
                                <p className="mt-4 border-t border-white/10 pt-3 text-[10px] leading-relaxed text-white/40">{s.cite}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 text-center text-xs uppercase tracking-widest text-white/50" data-testid="science-footer">
                        Every principle above · baked into your training and nutrition plan · from day one
                    </p>
                </div>
            </section>

            {/* ANCHORING ROW — puts our price in cognitive contrast with the
                real alternatives lifters spend money on. Every number is a
                verifiable, conservative benchmark. Placed pre-FAQ so it
                answers "is this worth it?" before the visitor even asks. */}
            <section className="border-b border-white/10 bg-[#0a0a0a]" data-testid="landing-anchor">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                    <div className="max-w-2xl">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">The math</div>
                        <h2 className="font-display text-4xl font-black uppercase leading-[1] sm:text-5xl">Cheaper than the coffee you drink pre-workout.</h2>
                        <p className="mt-4 text-sm text-white/70 sm:text-base">
                            The lifters getting real results usually pay for a coach, a coaching app, or years of trial-and-error. Here's what those cost.
                        </p>
                    </div>
                    <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[
                            { label: "Personal trainer", price: "$200+", per: "/ session", faded: true },
                            { label: "Online coach", price: "$150", per: "/ month", faded: true },
                            { label: "Coaching app", price: "$30", per: "/ month", faded: true },
                            { label: "Hypergains", price: `~${perDayAnnual}`, per: `(${currency})`, faded: false },
                        ].map((a, i) => (
                            <div
                                key={i}
                                className={`border p-5 ${a.faded ? "border-white/10 bg-white/[0.02]" : "border-[#ff3b30] bg-[#ff3b30]/10 shadow-[0_0_40px_-12px_rgba(239,68,68,0.4)]"}`}
                                data-testid={`landing-anchor-${i}`}
                            >
                                <div className={`text-[10px] uppercase tracking-widest ${a.faded ? "text-white/50" : "text-[#ff3b30]"}`}>{a.label}</div>
                                <div className="mt-1 flex items-baseline gap-1">
                                    <span className={`font-display text-3xl font-black tabular-nums ${a.faded ? "text-white/70" : "text-[#ff3b30]"}`}>{a.price}</span>
                                </div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-white/50">{a.per}</div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-xs uppercase tracking-widest text-white/50">
                        Same evidence base · same rep ranges · same principles · a fraction of the cost
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="border-b border-white/10 bg-[#0a0a0a]" data-testid="landing-faq">
                <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
                    <div className="mb-10">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">Straight answers</div>
                        <h2 className="font-display text-4xl font-black uppercase leading-[1] sm:text-5xl">Questions, not objections.</h2>
                    </div>
                    <Accordion type="single" collapsible className="border-t border-white/10">
                        {FAQ.map((item, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                className="border-b border-white/10"
                                data-testid={`faq-item-${i}`}
                            >
                                <AccordionTrigger className="py-5 text-left font-display text-base font-bold uppercase tracking-wide text-white hover:no-underline sm:text-lg">
                                    {item.q}
                                </AccordionTrigger>
                                <AccordionContent className="pb-6 text-sm text-white/70 sm:text-base">
                                    {item.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* CTA */}
            <section>
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
                    <div className="border border-[#ff3b30] bg-[#141414] p-10 sm:p-16">
                        <div className="label-eyebrow mb-3 text-[#ff3b30]">Everything for ~{perDay} {currency}</div>
                        <h3 className="font-display text-4xl font-black uppercase sm:text-5xl">Train less. Grow more.</h3>
                        <p className="mt-4 max-w-xl text-white/70">Your first 7 days unlock every program, every exercise, every education module, every rank, every PR tracker. No credit card. If you don&apos;t love it, you close the tab and never get charged.</p>
                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            <Link to="/onboarding" data-testid="cta-trial" className="rounded-sm bg-[#ff3b30] px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-[#d32f26]">Start my free 7 days</Link>
                            <Link to="/pricing" data-testid="cta-pricing" className="rounded-sm border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/5">See plans · from ~{perDayAnnual}</Link>
                        </div>
                        <p className="mt-5 text-xs uppercase tracking-widest text-white/50">No card required · Cancel in 2 taps · Zero data lost if you leave</p>
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 pb-24 md:pb-0">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 text-xs text-white/50 sm:px-6">
                    <span>© {new Date().getFullYear()} Hypergains</span>
                    <span className="uppercase tracking-widest">Stimulate · Don't annihilate</span>
                </div>
            </footer>

            {/* Sticky mobile CTA — only on small screens. Keeps the trial button reachable from any scroll position. */}
            <div
                className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#0a0a0a]/95 px-4 py-3 backdrop-blur md:hidden"
                data-testid="sticky-mobile-cta"
            >
                <div className="mx-auto flex max-w-md flex-col gap-1.5">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/onboarding"
                            data-testid="sticky-cta-trial"
                            className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-[#ff3b30] px-4 py-3 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#d32f26]"
                        >
                            Start my free 7 days <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                        <Link
                            to="/pricing"
                            data-testid="sticky-cta-pricing"
                            className="rounded-sm border border-white/20 px-3 py-3 text-xs font-bold uppercase tracking-wider text-white/80 hover:bg-white/5"
                        >
                            Plans
                        </Link>
                    </div>
                    <p className="text-center text-[9px] uppercase tracking-[0.16em] text-white/50">No card · Cancel in 2 taps · ~{perDayAnnual} {currency} on annual</p>
                </div>
            </div>
        </div>
    );
}
